import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'

export interface WorkspaceMember {
  id: string
  name: string
  email: string
  avatar: string | null
  role: string
  joined_at: string
}

export interface Workspace {
  id: string
  name: string
  slug: string
  description: string | null
  icon?: string | null
  owner_id: string
  invite_code?: string
  current_user_role: string | null
  is_owner: boolean
  can_manage: boolean
  members_count: number
  projects_count: number
  created_at: string
  updated_at: string
  members?: WorkspaceMember[]
}

export interface CreateWorkspaceData {
  name: string
  description?: string
}

export const useWorkspaceStore = defineStore('workspace', () => {
  // State
  const workspaces = ref<Workspace[]>([])
  const currentWorkspace = ref<Workspace | null>(null)
  const isLoading = ref(false)

  // Getters
  const hasWorkspaces = computed(() => workspaces.value.length > 0)

  // Actions
  async function fetchWorkspaces() {
    isLoading.value = true
    try {
      const response = await api.get('/workspaces')
      workspaces.value = response.data.data
    } catch (error) {
      console.error('Failed to fetch workspaces:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchWorkspace(slug: string) {
    isLoading.value = true
    try {
      const response = await api.get(`/workspaces/${slug}`)
      currentWorkspace.value = response.data.data
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch workspace:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function createWorkspace(data: CreateWorkspaceData) {
    isLoading.value = true
    try {
      const response = await api.post('/workspaces', data)
      const newWorkspace = response.data.data
      workspaces.value.push(newWorkspace)
      return { success: true, workspace: newWorkspace }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to create workspace',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function updateWorkspace(id: string, data: Partial<CreateWorkspaceData>) {
    isLoading.value = true
    try {
      const response = await api.put(`/workspaces/${id}`, data)
      const updatedWorkspace = response.data.data
      
      // Update in list
      const index = workspaces.value.findIndex((w) => w.id === id)
      if (index !== -1) {
        workspaces.value[index] = updatedWorkspace
      }
      
      // Update current if same
      if (currentWorkspace.value?.id === id) {
        currentWorkspace.value = updatedWorkspace
      }
      
      return { success: true, workspace: updatedWorkspace }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to update workspace',
      }
    } finally {
      isLoading.value = false
    }
  }

  async function deleteWorkspace(id: string) {
    isLoading.value = true
    try {
      await api.delete(`/workspaces/${id}`)
      workspaces.value = workspaces.value.filter((w) => w.id !== id)
      
      if (currentWorkspace.value?.id === id) {
        currentWorkspace.value = null
      }
      
      return { success: true }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      return {
        success: false,
        message: err.response?.data?.message || 'Failed to delete workspace',
      }
    } finally {
      isLoading.value = false
    }
  }

  function setCurrentWorkspace(workspace: Workspace | null) {
    currentWorkspace.value = workspace
  }

  return {
    // State
    workspaces,
    currentWorkspace,
    isLoading,
    // Getters
    hasWorkspaces,
    // Actions
    fetchWorkspaces,
    fetchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    setCurrentWorkspace,
  }
})
